import { emailRegex, TEST_IDS } from '@va/constants';
import { CheckmarkIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { useUnsavedChangesGuard } from '@va/shared/util-confirmation';
import { SubSectionHeader } from '@va/ui/components/white-label-customisation';
import { ActionInput, Button } from '@va/ui/design-system';
import { useMemo } from 'react';
import useDummyEmail from './useDummyEmail';
import useReplyToEmailAddress, { fieldName } from './useReplyToEmailAddress';

export const ReplyToEmailSection = () => {
  const { values, handleChange, clearField, handleSubmit, formErrors, isLoading, agencyUiId, dirty } =
    useReplyToEmailAddress();

  const { dummyEmail, setDummyEmail, sendDummyEmail, isSendDummyApiLoading } = useDummyEmail();

  const isSaveButtonDisabled = isLoading || !agencyUiId || !dirty;

  const isDummyEmailFieldHasError = useMemo(() => !!dummyEmail && !dummyEmail.match(emailRegex), [dummyEmail]);
  const translate = useTranslate();

  useUnsavedChangesGuard(dirty, { id: 'reply-to' });

  return (
    <div>
      <SubSectionHeader
        tooltip={translate('whiteLabel.email.replyTo.tooltip')}
        text={translate('whiteLabel.email.replyTo.title')}
      />

      <form onSubmit={handleSubmit}>
        <ActionInput
          buttonProps={{
            icon: () => <CheckmarkIcon color='white' />,
            text: translate('button.confirm'),
          }}
          textInputProps={{
            error: formErrors.replyToEmailAddress,
            name: fieldName,
            value: values.replyToEmailAddress ?? '',
            onChange: handleChange,
            clearField: clearField,
            label: translate('form.labels.addReplyToEmail'),
            placeholder: translate('card.notifications.placeholders.email'),
          }}
          info={translate('whiteLabel.email.replyTo.info')}
        />

        <Button
          type={isSaveButtonDisabled ? 'button' : 'submit'}
          color='secondary'
          disabled={isSaveButtonDisabled}
          text={translate('button.confirmReplyToEmail')}
          className='w-full my-5'
          tooltip={!agencyUiId ? translate('whiteLabelCustomization.info.firstFillCustomUrl') : undefined}
          data-testid={TEST_IDS.generic.whiteLabelTheme.confirmReplyToEmailBtn}
        />
      </form>

      <SubSectionHeader
        tooltip={
          <div className='flex flex-col'>
            {translate('standalone.accountSettings.whiteLabelTheme.customEmailAddress.dummyEmailStepsTooltip.title')}
          </div>
        }
        text={translate('whiteLabel.email.sendDummy.title')}
      />
      <ActionInput
        info={translate('standalone.accountSettings.whiteLabelTheme.customEmailAddress.testEmailInputFieldInfo')}
        buttonProps={{
          onClick: () => !isDummyEmailFieldHasError && sendDummyEmail(dummyEmail),
          text: isSendDummyApiLoading ? translate('button.loading') : translate('button.send'),
          disabled: isSendDummyApiLoading || !agencyUiId,
          tooltip: !agencyUiId ? translate('whiteLabelCustomization.info.firstFillCustomUrl') : undefined,
          'data-testid': TEST_IDS.generic.whiteLabelTheme.sendTestEmailBtn,
        }}
        textInputProps={{
          error: isDummyEmailFieldHasError ? translate('register.form.email.invalidMessage') : undefined,
          value: dummyEmail,
          name: 'newEmailAddress',
          onChange: (e) => setDummyEmail(e.target.value),
          clearField: () => setDummyEmail(''),
          label: translate('form.labels.addTestEmail'),
          placeholder: translate('card.notifications.placeholders.email'),
        }}
      />
    </div>
  );
};
